import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import toast from "react-hot-toast";
import { generateFullPDF } from "./utils/generateFullPDF";
import { startCamera, stopCamera, capturePhoto } from "./utils/camera";
import html2canvas from "html2canvas";
import MemberCard from "./components/MemberCard";
import "./member-page-pro.css";
import DocumentIntakeModal from "./DocumentIntakeModal";

axios.defaults.withCredentials = true;

function MemberPage() {
  const loadImageAsBase64 = (url) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        if (!img.width || !img.height) {
          console.error("❌ Image has no size:", url);
          return resolve(null);
        }

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        try {
          const base64 = canvas.toDataURL("image/png");

          if (base64.length < 100) {
            console.error("❌ Base64 too small (invalid):", base64);
            return resolve(null);
          }

          resolve(base64);
        } catch (err) {
          console.error("❌ Canvas error:", err);
          resolve(null);
        }
      };

      img.onerror = () => {
        console.error("❌ Image failed to load:", url);
        resolve(null);
      };

      img.src = url;
    });

  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [comments, setComments] = useState([]);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [commentsLoading, setCommentsLoading] = useState(false);
  // Per-comment translations are UI-only; the saved original comment is never changed.
  const [commentTranslations, setCommentTranslations] = useState({});
  const [translatingComments, setTranslatingComments] = useState({});

  const [documents, setDocuments] = useState([]);
  const [showDocumentIntake, setShowDocumentIntake] = useState(false);
  const [newDocId, setNewDocId] = useState(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [newName, setNewName] = useState("");
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [renewalData, setRenewalData] = useState({
    renewal_date: "",
    new_expiration: "",
  });
  const [renewals, setRenewals] = useState([]);
  const chatEndRef = useRef(null);
  const [qrCode, setQrCode] = useState("");
  const [settings, setSettings] = useState({});
  const [editing, setEditing] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem("admin");
    localStorage.removeItem("member");
    navigate("/");
  };

  const handleDownload = async () => {
    if (!member) return;

    // ✅ PHOTO
    let photoBase64 = null;

    if (member.photo) {
      photoBase64 = await loadImageAsBase64(
        `/api/image.php?file=${member.photo}`,
      );
    }

    // ✅ ADMIN SIGNATURE
    const res = await axios.get("/api/getAdminSignature.php");

    const adminSignatureFile = res.data.signature;

    let adminSignatureBase64 = null;

    if (adminSignatureFile) {
      adminSignatureBase64 = await loadImageAsBase64(
        `/api/image.php?file=${adminSignatureFile}`,
      );
    }

    // ✅ MEMBER SIGNATURE (if stored)
    let memberSignature = null;

    if (member.signature) {
      memberSignature = await loadImageAsBase64(
        `/api/image.php?file=${member.signature}`,
      );
    }

    await generateFullPDF(
      {
        ...member,
        signature: memberSignature, // ✅ now base64
        admin_signature_file: adminSignatureFile,
      },
      photoBase64,
    );
    //console.log("PHOTO BASE64:", photoBase64);
    console.log(photoBase64?.slice(0, 30));
    console.log("PHOTO LENGTH:", photoBase64?.length);
    console.log("SIGNATURE:", member.signature);
  };

  const handleSendPDF = async () => {
    if (!member) return;

    const confirmSend = window.confirm(`Envoyer la fiche à ${member.email} ?`);

    if (!confirmSend) return;

    try {
      // ✅ PHOTO
      let photoBase64 = null;

      if (member.photo) {
        photoBase64 = await loadImageAsBase64(
          `/api/image.php?file=${member.photo}`,
        );
      }

      // ✅ MEMBER SIGNATURE
      let memberSignature = null;

      if (member.signature) {
        memberSignature = await loadImageAsBase64(
          `/api/image.php?file=${member.signature}`,
        );
      }

      // ✅ ADMIN SIGNATURE
      const res = await axios.get("/api/getAdminSignature.php");

      const adminSignatureFile = res.data.signature;

      // ✅ GENERATE PDF (WITHOUT DOWNLOAD)
      const pdfBlob = await generateFullPDF(
        {
          ...member,
          signature: memberSignature,
          admin_signature_file: adminSignatureFile,
        },
        photoBase64,
        true, // 🔥 IMPORTANT (no download mode)
      );

      // ✅ SEND TO BACKEND
      const formData = new FormData();
      formData.append("pdf", pdfBlob, "fiche.pdf");
      formData.append("email", member.email);
      formData.append("nom", member.nom);
      formData.append("prenom", member.prenom);

      const response = await axios.post("/api/sendCard.php", formData);

      if (response.data.success) {
        toast.success("📧 Fiche envoyée avec succès !");
      } else {
        toast.error("Erreur envoi ❌");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur ❌");
    }
  };

  //==========================================================//
  // MESSAGE THINGS
  //===========================================================
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unread, setUnread] = useState(0);

  const loadMessages = async () => {
    try {
      const res = await axios.get(
        `/api/getMessages.php?member_id=${id}&role=admin`,
      );

      setMessages(res.data.messages);
      setUnread(res.data.unread);
    } catch (err) {
      console.error(err);
    }
  };

  const openChat = async () => {
    setShowChat(true);

    await axios.post(
      "/api/markAsRead.php",
      { member_id: id }, // 🔥 THIS IS THE FIX
      { withCredentials: true },
    );
    loadMessages();
  };

  useEffect(() => {
    axios
      .get(`/api/getRenewals.php?member_id=${id}`)
      .then((res) => setRenewals(res.data));
  }, [id]);

  const translateMemberComment = async (comment, targetLanguage) => {
    const commentId = String(
      comment.id ??
        comment.comment_id ??
        comment.created_at ??
        comment.commentaire,
    );
    const originalText = String(comment.commentaire || "").trim();
    if (!originalText) return;

    const cached = commentTranslations[commentId] || {};
    if (cached[targetLanguage]) {
      setCommentTranslations((prev) => ({
        ...prev,
        [commentId]: { ...prev[commentId], selectedLanguage: targetLanguage },
      }));
      return;
    }

    setTranslatingComments((prev) => ({ ...prev, [commentId]: true }));
    try {
      // Same Google Translate endpoint and per-comment UI behavior as Admin.
      const params = new URLSearchParams({
        client: "gtx",
        sl: "auto",
        tl: targetLanguage,
        dt: "t",
        q: originalText,
      });
      const response = await fetch(
        `https://translate.googleapis.com/translate_a/single?${params.toString()}`,
      );
      if (!response.ok)
        throw new Error(`Translation request failed (${response.status})`);
      const result = await response.json();
      const translatedText = Array.isArray(result?.[0])
        ? result[0].map((part) => (Array.isArray(part) ? part[0] : "")).join("")
        : "";
      if (!translatedText) throw new Error("Empty translation response");

      setCommentTranslations((prev) => ({
        ...prev,
        [commentId]: {
          ...prev[commentId],
          [targetLanguage]: translatedText,
          selectedLanguage: targetLanguage,
        },
      }));
    } catch (error) {
      console.error("COMMENT TRANSLATION ERROR:", error);
      toast.error(
        "Traduction impossible. Vérifiez votre connexion internet et réessayez.",
      );
    } finally {
      setTranslatingComments((prev) => ({ ...prev, [commentId]: false }));
    }
  };

  const loadMemberComments = async () => {
    setCommentsLoading(true);
    try {
      const res = await axios.get(`/api/getComments.php?id=${id}`);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setComments(data);
    } catch (err) {
      console.error("LOAD MEMBER COMMENTS ERROR:", err);
      toast.error("Impossible de charger les commentaires.");
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const openMemberComments = async () => {
    setShowCommentsModal(true);
    await loadMemberComments();
  };

  const addMemberComment = async () => {
    if (!newCommentText.trim()) {
      toast.error("Écris un commentaire avant de l’ajouter.");
      return;
    }
    const toastId = toast.loading("Ajout du commentaire...");
    try {
      await axios.post("/api/addComment.php", {
        id: member.id || id,
        commentaire: newCommentText.trim(),
      });
      setNewCommentText("");
      await loadMemberComments();
      toast.success("Commentaire ajouté.", { id: toastId });
    } catch (err) {
      console.error("ADD MEMBER COMMENT ERROR:", err);
      toast.error("Erreur lors de l’ajout du commentaire.", { id: toastId });
    }
  };

  // 🔥 LOAD MEMBER
  useEffect(() => {
    axios
      .get(`/api/getMember.php?id=${id}`)
      .then((res) => {
        if (res.data.success) {
          const rawMember = res.data.member || {};

          // Normalize the payment fields here so the PDF generator always
          // receives the same field names, regardless of whether the API
          // returns MySQL snake_case or App.js camelCase names.
          const normalizedMember = {
            ...rawMember,
            cotisation:
              rawMember.cotisation ??
              rawMember.cotisation_amount ??
              rawMember.cotisationAmount ??
              "",
            cotisationStatus:
              rawMember.cotisationStatus ??
              rawMember.cotisation_status ??
              rawMember.cotisationStatusValue ??
              "",
            cotisationAmount:
              rawMember.cotisationAmount !== undefined &&
              rawMember.cotisationAmount !== null &&
              String(rawMember.cotisationAmount).trim() !== ""
                ? rawMember.cotisationAmount
                : rawMember.cotisation_paid !== undefined &&
                    rawMember.cotisation_paid !== null &&
                    String(rawMember.cotisation_paid).trim() !== ""
                  ? rawMember.cotisation_paid
                  : (rawMember.cotisation_amount_paid ?? ""),
            cotisation_remaining:
              rawMember.cotisation_remaining !== undefined &&
              rawMember.cotisation_remaining !== null &&
              String(rawMember.cotisation_remaining).trim() !== ""
                ? rawMember.cotisation_remaining
                : (rawMember.cotisationRemaining ?? ""),
          };

          console.log("MEMBER PAYMENT DATA:", {
            cotisation: normalizedMember.cotisation,
            cotisationStatus: normalizedMember.cotisationStatus,
            cotisationAmount: normalizedMember.cotisationAmount,
            cotisation_remaining: normalizedMember.cotisation_remaining,
            rawMember,
          });

          setMember(normalizedMember);
          setComments(res.data.comments);
        }
      })
      .catch(console.error);

    loadMessages(); // ✅ outside axios
  }, [id]);

  useEffect(() => {
    axios
      .get(`/api/getDocuments.php?member_id=${id}`)
      .then((res) => setDocuments(res.data));
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadMessages();
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    axios.get("/api/getSettings.php").then((res) => {
      setSettings(res.data.settings || {});
    });
  }, []);

  const sendAdminMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await axios.post(
        "/api/sendMessage.php",
        {
          member_id: id, // 🔥 ADD THIS
          message: newMessage,
        },
        { withCredentials: true },
      );
      setNewMessage("");
      loadMessages();
    } catch (err) {
      console.error(err);
    }
  };

  const getSuiviClass = (value) => {
    if (!value) return "neutral";

    const str = Array.isArray(value) ? value.join(" ") : value;

    if (str.includes("OQTF") || str.includes("IRTF")) return "danger";
    if (str.includes("Sans papiers")) return "warning";
    if (str.includes("Demandeur")) return "info";
    if (str.includes("Situation régulière")) return "success";

    return "neutral";
  };

  //=======================//
  //   TASKS MANAGEMENT   //
  //=====================//

  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    due_date: "",
  });

  //***************************************************************** */
  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx/
  //***************************************************************** */

  useEffect(() => {
    axios.get(`/api/getTasks.php?member_id=${id}`).then((res) => {
      console.log("TASKS RESPONSE:", res.data); // 👈 DEBUG
      setTasks(res.data || []);
    });
  }, [id]);

  useEffect(() => {
    const generateQR = async () => {
      if (!member?.numero_membre) return;

      const qr = await QRCode.toDataURL(member.numero_membre);

      setQrCode(qr);
    };

    generateQR();
  }, [member]);

  const addTask = async () => {
    if (!newTask.title) return;

    const formData = new FormData();
    formData.append("member_id", id);
    formData.append("title", newTask.title);
    formData.append("description", newTask.description);
    formData.append("due_date", newTask.due_date);

    await axios.post("/api/addTask.php", formData);

    setNewTask({ title: "", description: "", due_date: "" });

    const res = await axios.get(`/api/getTasks.php?member_id=${id}`);

    setTasks(res.data || []);
  };

  const toggleTask = async (task) => {
    const newStatus = task.status === "done" ? "pending" : "done";

    const formData = new FormData();
    formData.append("id", task.id);
    formData.append("status", newStatus);

    await axios.post("/api/updateTaskStatus.php", formData);

    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, status: newStatus } : t)),
    );
  };
  const deleteTask = async (idTask) => {
    if (!window.confirm("Supprimer cette tâche ?")) return;

    const formData = new FormData();
    formData.append("id", idTask);

    await axios.post("/api/deleteTask.php", formData);

    setTasks((prev) => prev.filter((t) => t.id !== idTask));
  };

  const getTaskStatus = (task) => {
    if (task.status === "done") return "done";

    if (!task.due_date) return "normal";

    const now = new Date();
    const due = new Date(task.due_date);

    // remove time for clean compare
    now.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    const diffTime = due - now;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    if (diffDays < 0) return "expired"; // 🔴
    if (diffDays <= 2) return "soon"; // 🟠 (48h)

    return "active"; // 🟢
  };

  // ⛔ DO NOT TOUCH member BEFORE THIS
  if (!member) return <p>Chargement...</p>;

  // ✅ NOW SAFE
  let type = member.typeAdhesion;

  try {
    if (typeof type === "string" && type.startsWith("[")) {
      type = JSON.parse(type);
    }
  } catch (e) {}

  const displayType = Array.isArray(type)
    ? type.join(", ")
    : type || "Non défini";

  // ✅ REAL EXPIRATION (renewals override)
  let realExpiration = member.dateExpiration;

  if (renewals.length > 0) {
    const lastRenewal = renewals[renewals.length - 1];
    if (lastRenewal?.new_expiration) {
      realExpiration = lastRenewal.new_expiration;
    }
  }

  // ✅ ROBUST EXPIRATION CHECK
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let isExpired = false;

  if (realExpiration) {
    const exp = new Date(realExpiration);
    exp.setHours(0, 0, 0, 0);

    isExpired = exp < today;
  }

  const downloadCard = async () => {
    const card = document.getElementById("member-card");

    const canvas = await html2canvas(card, {
      scale: 3,
    });

    const img = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: [85, 55],
    });

    pdf.addImage(img, "PNG", 0, 0, 85, 55);

    pdf.save(`carte_${member.numero_membre}.pdf`);
  };

  return (
    <div className="mp-shell">
      <aside className="admin-sidebar mp-admin-sidebar">
        <div className="sidebar-brand-row">
          <div className="sidebar-logo-circle">
            <img
              src="/logo.png"
              alt="United Migrants"
              className="sidebar-logo-image"
            />
          </div>
          <button
            className="sidebar-collapse"
            type="button"
            aria-label="Réduire le menu"
            onClick={(e) => {
              e.currentTarget
                .closest(".admin-sidebar")
                ?.classList.toggle("sidebar-collapsed");
            }}
          >
            <span aria-hidden="true">‹</span>
          </button>
        </div>

        <button
          className="sidebar-card"
          type="button"
          onClick={() => navigate("/admin")}
        >
          <span className="sidebar-icon" aria-hidden="true">
            ▦
          </span>
          <span>Tableau de bord</span>
        </button>
        <button
          className="sidebar-card sidebar-card-active"
          type="button"
          aria-current="page"
          onClick={() => navigate("/admin")}
        >
          <span className="sidebar-icon" aria-hidden="true">
            ♟
          </span>
          <span>Membres</span>
        </button>
        <button
          className="sidebar-card"
          type="button"
          onClick={() => {
            const taskSection = document.querySelector(".mp-tasks-card");
            if (!taskSection) return;

            // Bring the current member's task list into view.
            taskSection.scrollIntoView({ behavior: "smooth", block: "start" });
            taskSection.classList.add("mp-tasks-highlight");
            window.setTimeout(() => {
              taskSection.classList.remove("mp-tasks-highlight");
            }, 1400);
          }}
        >
          <span className="sidebar-icon" aria-hidden="true">
            ☑
          </span>
          <span>Tâches</span>
        </button>
        <button
          className="sidebar-card"
          type="button"
          onClick={() => {
            const documentsSection = document.querySelector(".mp-docs-card");
            documentsSection?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
            documentsSection?.classList.add("mp-section-highlight");
            window.setTimeout(
              () => documentsSection?.classList.remove("mp-section-highlight"),
              1400,
            );
          }}
        >
          <span className="sidebar-icon" aria-hidden="true">
            ▤
          </span>
          <span>Documents</span>
        </button>

        <button
          className="sidebar-card"
          type="button"
          onClick={openMemberComments}
        >
          <span className="sidebar-icon" aria-hidden="true">
            ●
          </span>
          <span>Commentaires</span>
        </button>

        <div className="sidebar-user">
          <div className="sidebar-user-avatar" aria-hidden="true">
            ♙
          </div>
          <div className="sidebar-user-info">
            <strong>Administrateur</strong>
            <small>Gestion des membres</small>
          </div>
          <button
            className="sidebar-logout"
            type="button"
            title="Déconnexion"
            onClick={handleLogout}
            aria-label="Déconnexion"
          >
            ↪
          </button>
        </div>
      </aside>

      <main className="mp-main">
        <div className="mp-topbar">
          <div className="mp-crumb">
            Membres <span>›</span> <strong>Fiche membre</strong>
          </div>
          <div className="mp-top-actions">
            <div className="mp-search">
              ⌕ <span>Rechercher un membre...</span>
            </div>
            <button className="mp-bell" aria-label="Notifications">
              ♧<i>3</i>
            </button>
          </div>
        </div>
        <div className="mp-page-heading">
          <div>
            <h1>Fiche membre</h1>
            <button className="mp-back" onClick={() => navigate("/admin")}>
              ← Retour aux membres
            </button>
          </div>
          <div className="mp-heading-actions">
            <button
              className="mp-outline"
              onClick={() => {
                let suivi = member.suivi_administratif;
                try {
                  if (typeof suivi === "string" && suivi.startsWith("["))
                    suivi = JSON.parse(suivi);
                  else if (typeof suivi === "string" && suivi) suivi = [suivi];
                  else if (!Array.isArray(suivi)) suivi = [];
                } catch {
                  suivi = [];
                }
                let types = member.typeAdhesion;
                try {
                  if (typeof types === "string" && types.startsWith("["))
                    types = JSON.parse(types);
                  else if (typeof types === "string" && types) types = [types];
                  else if (!Array.isArray(types)) types = [];
                } catch {
                  types = [];
                }
                setEditing({
                  ...member,
                  suivi_administratif: suivi,
                  typeAdhesion: types,
                });
              }}
            >
              ✎ &nbsp; Modifier la fiche
            </button>
            <button
              className="mp-more"
              title="Actions"
              onClick={() => toast("Actions supplémentaires")}
            >
              •••
            </button>
          </div>
        </div>

        <section className="mp-profile mp-card">
          {member.photo ? (
            <img
              src={`/api/image.php?file=${member.photo}`}
              alt="Photo du membre"
              className="mp-photo"
            />
          ) : (
            <div className="mp-photo mp-photo-placeholder">♙</div>
          )}
          <div className="mp-profile-name">
            <h2>
              {member.nom} {member.prenom}
            </h2>
            <p>N° : {member.numero_membre || "—"}</p>
            <span className={`mp-status ${isExpired ? "is-expired" : ""}`}>
              <i />
              {isExpired ? "Expiré" : "Actif"}
            </span>
          </div>
          <div className="mp-profile-dates">
            <span className="mp-calendar">▦</span>
            <div>
              <p>
                Adhésion : <strong>{member.adhesion || "—"}</strong>
              </p>
              <p>
                Expire le :{" "}
                <strong className="mp-orange">{realExpiration || "—"}</strong>
              </p>
            </div>
          </div>
          <button className="mp-primary mp-message-btn" onClick={openChat}>
            ▣ &nbsp; Envoyer un message{" "}
            {unread > 0 && <b className="mp-unread">{unread}</b>}
          </button>
        </section>

        <div className="mp-grid mp-grid-three">
          <section className="mp-card mp-info-card">
            <h3>
              <span className="mp-icon">♟</span>Informations personnelles
            </h3>
            <dl>
              <dt>Sexe :</dt>
              <dd>{member.gender || "—"}</dd>
              <dt>Date de naissance :</dt>
              <dd>{member.naissance || "—"}</dd>
              <dt>Profession :</dt>
              <dd>{member.metier || "—"}</dd>
              <dt>Hébergement :</dt>
              <dd>{member.hebergement || "—"}</dd>
            </dl>
          </section>
          <section className="mp-card mp-info-card">
            <h3>
              <span className="mp-icon">☎</span>Coordonnées
            </h3>
            <dl>
              <dt>Email :</dt>
              <dd>{member.email || "—"}</dd>
              <dt>Téléphone :</dt>
              <dd>{member.contact || "—"}</dd>
              <dt>Adresse :</dt>
              <dd>{member.adresse || "—"}</dd>
              <dt>Code postal :</dt>
              <dd>{member.codePostal || "—"}</dd>
              <dt>Commune :</dt>
              <dd>{member.commune || "—"}</dd>
            </dl>
          </section>
          <section className="mp-card mp-info-card">
            <h3>
              <span className="mp-icon">▤</span>Adhésion
            </h3>
            <dl>
              <dt>Date d’adhésion :</dt>
              <dd>{member.adhesion || "—"}</dd>
              <dt>Date d’expiration :</dt>
              <dd>{realExpiration || "—"}</dd>
              <dt>Type d’adhésion :</dt>
              <dd>{displayType}</dd>
            </dl>
            <div className={`mp-validity ${isExpired ? "expired" : ""}`}>
              <span>{isExpired ? "!" : "✓"}</span>
              <div>
                <strong>
                  {isExpired ? "Adhésion expirée" : "Membre actif"}
                </strong>
                <small>
                  {isExpired
                    ? "Un renouvellement est nécessaire."
                    : "L’adhésion est en cours de validité."}
                </small>
              </div>
            </div>
          </section>
        </div>

        <div className="mp-grid mp-grid-three mp-grid-secondary">
          <section className="mp-card">
            <h3>
              <span className="mp-icon">▣</span>Suivi administratif
            </h3>
            <div className="mp-empty">
              <span className="mp-empty-icon">▤</span>
              <p>
                {(() => {
                  let suivi = member.suivi_administratif;
                  try {
                    if (typeof suivi === "string" && suivi.startsWith("["))
                      suivi = JSON.parse(suivi);
                  } catch (e) {}
                  return Array.isArray(suivi)
                    ? suivi.join(", ")
                    : suivi || "Aucun suivi administratif pour le moment.";
                })()}
              </p>
              <span
                className={`mp-follow-tag ${getSuiviClass(member.suivi_administratif)}`}
              >
                Suivi du dossier
              </span>
            </div>
          </section>
          <section className="mp-card">
            <h3>
              <span className="mp-icon">⟳</span>Historique des renouvellements
            </h3>
            {renewals.length ? (
              <div className="mp-timeline">
                {renewals.map((r) => {
                  const isAdhesion =
                    member.adhesion && r.renewal_date === member.adhesion;
                  return (
                    <div className="mp-timeline-item" key={r.id}>
                      <i />
                      <strong>
                        {isAdhesion ? "Adhésion" : "Renouvellement"}
                      </strong>
                      <p>
                        {r.renewal_date} → {r.new_expiration}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mp-empty compact">
                <span className="mp-empty-icon">⟳</span>
                <p>Aucun historique de renouvellement.</p>
              </div>
            )}
            {isExpired && (
              <div className="mp-renew-form">
                <label>
                  Date de renouvellement
                  <input
                    type="date"
                    value={renewalData.renewal_date || ""}
                    onChange={(e) => {
                      const renewalDate = e.target.value;
                      const date = new Date(`${renewalDate}T12:00:00`);
                      date.setFullYear(date.getFullYear() + 1);
                      setRenewalData({
                        renewal_date: renewalDate,
                        new_expiration: renewalDate
                          ? date.toISOString().slice(0, 10)
                          : "",
                      });
                    }}
                  />
                </label>
                <label>
                  Nouvelle expiration
                  <input
                    type="date"
                    value={renewalData.new_expiration || ""}
                    readOnly
                  />
                </label>
                <button
                  className="mp-primary"
                  onClick={async () => {
                    const formData = new FormData();
                    formData.append("member_id", id);
                    formData.append("renewal_date", renewalData.renewal_date);
                    formData.append(
                      "new_expiration",
                      renewalData.new_expiration,
                    );
                    await axios.post("/api/renewMember.php", formData);
                    toast.success("Renouvellement effectué");
                    setRenewalData({ renewal_date: "", new_expiration: "" });
                    const rm = await axios.get(`/api/getMember.php?id=${id}`);
                    if (rm.data.success) setMember(rm.data.member);
                    const rr = await axios.get(
                      `/api/getRenewals.php?member_id=${id}`,
                    );
                    setRenewals(rr.data);
                  }}
                >
                  Valider le renouvellement
                </button>
              </div>
            )}
          </section>
          <section className="mp-card">
            <h3>
              <span className="mp-icon">●</span>Commentaires
            </h3>
            {comments.length ? (
              <div className="mp-comments">
                {comments.map((c) => (
                  <div className="mp-comment" key={c.id}>
                    <small>
                      {new Date(c.created_at).toLocaleString("fr-FR")}
                    </small>
                    <p>{c.commentaire}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mp-empty">
                <span className="mp-empty-icon">▱</span>
                <p>Aucun commentaire pour le moment.</p>
              </div>
            )}
          </section>
        </div>

        <div className="mp-grid mp-bottom-grid">
          <section className="mp-card mp-tasks-card">
            <h3>
              <span className="mp-icon">⚑</span>Suivi / Tâches
            </h3>
            <div className="mp-task-form">
              <input
                placeholder="Titre de la tâche..."
                value={newTask.title}
                onChange={(e) =>
                  setNewTask({ ...newTask, title: e.target.value })
                }
              />
              <input
                placeholder="Description..."
                value={newTask.description}
                onChange={(e) =>
                  setNewTask({ ...newTask, description: e.target.value })
                }
              />
              <input
                aria-label="Date d'échéance"
                type="date"
                value={newTask.due_date}
                onChange={(e) =>
                  setNewTask({ ...newTask, due_date: e.target.value })
                }
              />
              <button className="mp-primary" onClick={addTask}>
                ＋ Ajouter la tâche
              </button>
            </div>
            {tasks.length ? (
              <div className="mp-task-list">
                {(Array.isArray(tasks) ? tasks : []).map((task) => (
                  <div
                    key={task.id}
                    className={`mp-task ${getTaskStatus(task)}`}
                  >
                    <div>
                      <strong>{task.title}</strong>
                      <p>{task.description}</p>
                      {task.due_date && (
                        <small>Échéance : {task.due_date}</small>
                      )}
                    </div>
                    <div className="mp-task-actions">
                      <button
                        title="Changer le statut"
                        onClick={() => toggleTask(task)}
                      >
                        {task.status === "done" ? "✓" : "◷"}
                      </button>
                      <button
                        title="Supprimer"
                        onClick={() => deleteTask(task.id)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mp-empty mp-task-empty">
                <span className="mp-empty-icon">☑</span>
                <p>Aucune tâche en cours.</p>
              </div>
            )}
          </section>
          <section className="mp-card mp-docs-card">
            <h3>
              <span className="mp-icon">▰</span>Documents
            </h3>
            <button
              type="button"
              className="mp-upload"
              onClick={() => setShowDocumentIntake(true)}
              style={{
                width: "100%",
                cursor: "pointer",
                border: "1px dashed #9ca3af",
                background: "transparent",
              }}
            >
              <span className="mp-upload-icon">⇧</span>
              <strong>Ajouter un fichier</strong>
              <small>
                Autres documents, preuves de présence ou fiches de paie
              </small>
              <em>Formats acceptés : PDF, JPG, PNG</em>
            </button>
            {documents.length > 0 && (
              <div className="mp-doc-list">
                {documents.map((doc) => {
                  const displayName = doc.original_name || doc.file_name;
                  return (
                    <div className="mp-doc-row" key={doc.id}>
                      <span>▤</span>
                      {editingDocId === doc.id ? (
                        <input
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                        />
                      ) : (
                        <span className="mp-doc-name" title={displayName}>
                          {displayName}
                        </span>
                      )}
                      <div>
                        {editingDocId === doc.id ? (
                          <button
                            onClick={async () => {
                              const fd = new FormData();
                              fd.append("id", doc.id);
                              fd.append("name", newName);
                              await axios.post("/api/renameDocument.php", fd);
                              setEditingDocId(null);
                              const d = await axios.get(
                                `/api/getDocuments.php?member_id=${id}`,
                              );
                              setDocuments(d.data || []);
                            }}
                          >
                            ✓
                          </button>
                        ) : (
                          <button
                            title="Renommer"
                            onClick={() => {
                              setEditingDocId(doc.id);
                              setNewName(displayName);
                            }}
                          >
                            ✎
                          </button>
                        )}
                        <a
                          title="Ouvrir"
                          href={`/api/image.php?file=${doc.file_name}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          ↗
                        </a>
                        <button
                          title="Supprimer"
                          onClick={async () => {
                            if (!window.confirm("Supprimer ce document ?"))
                              return;
                            try {
                              const fd = new FormData();
                              fd.append("id", doc.id);
                              await axios.post("/api/deleteDocument.php", fd);
                              const d = await axios.get(
                                `/api/getDocuments.php?member_id=${id}`,
                              );
                              setDocuments(d.data || []);
                              toast.success("Document supprimé");
                            } catch (err) {
                              toast.error("Erreur de suppression");
                            }
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mp-download-actions">
              <button onClick={downloadCard}>
                ▣ &nbsp; Télécharger la carte
              </button>
              <button className="mp-outline" onClick={handleDownload}>
                ▤ &nbsp; Télécharger la fiche
              </button>
            </div>
          </section>
        </div>

        {showDocumentIntake && (
          <DocumentIntakeModal
            memberId={id}
            onClose={() => setShowDocumentIntake(false)}
            onUploaded={async () => {
              const d = await axios.get(
                `/api/getDocuments.php?member_id=${id}`,
              );
              setDocuments(d.data || []);
            }}
          />
        )}

        <div className="mp-hidden-card">
          <MemberCard
            member={{
              ...member,
              card_adhesion:
                renewals.length > 0
                  ? renewals[renewals.length - 1].renewal_date
                  : member.adhesion,
              card_expiration:
                renewals.length > 0
                  ? renewals[renewals.length - 1].new_expiration
                  : member.dateExpiration,
            }}
            qrCode={qrCode}
            settings={settings}
          />
        </div>
        <button
          className="mp-password-reset"
          title="Réinitialiser le mot de passe"
          onClick={async () => {
            if (!window.confirm("Réinitialiser le mot de passe de ce membre ?"))
              return;
            const tid = toast.loading("Réinitialisation...");
            try {
              const fd = new FormData();
              fd.append("id", member.id);
              const res = await axios.post("/api/resetMemberPassword.php", fd);
              if (res.data?.error) throw new Error(res.data.error);
              toast.success("Réinitialisation effectuée", { id: tid });
            } catch (err) {
              console.error(err);
              toast.error(err.message || "Erreur serveur", { id: tid });
            }
          }}
        >
          🔑
        </button>
      </main>

      {editing && (
        <div
          className="modal-overlay"
          onClick={() => {
            setEditing(null);
            if (cameraOn) stopCamera(videoRef, setCameraOn);
          }}
        >
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Modifier membre</h3>
            <div className="photo-edit">
              <label>Photo</label>
              <img
                src={
                  editing.preview ||
                  (editing.photo
                    ? `/api/image.php?file=${editing.photo}`
                    : "/default-avatar.png")
                }
                alt="Aperçu"
                className="edit-photo-preview"
              />
              <input
                type="file"
                id="member-edit-photo"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onloadend = () =>
                    setEditing((prev) => ({
                      ...prev,
                      newPhoto: file,
                      preview: reader.result,
                    }));
                  reader.readAsDataURL(file);
                }}
              />
              <label htmlFor="member-edit-photo" className="upload-btn">
                📂 Upload
              </label>
              <button
                type="button"
                className="upload-btn"
                onClick={() => startCamera(videoRef, setCameraOn)}
              >
                📸 Camera
              </button>
              {cameraOn && (
                <div className="camera-box">
                  <video ref={videoRef} autoPlay playsInline />
                  <div className="camera-actions">
                    <button
                      type="button"
                      onClick={() =>
                        capturePhoto({
                          videoRef,
                          canvasRef,
                          setCameraOn,
                          onCapture: ({ file, preview }) =>
                            setEditing((prev) => ({
                              ...prev,
                              newPhoto: file,
                              preview,
                            })),
                        })
                      }
                    >
                      📷 Capturer
                    </button>
                    <button
                      type="button"
                      onClick={() => stopCamera(videoRef, setCameraOn)}
                    >
                      ❌ Fermer
                    </button>
                  </div>
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                </div>
              )}
            </div>
            <div className="edit-grid">
              {[
                ["Nom", "nom"],
                ["Prénom", "prenom"],
                ["Email", "email"],
                ["Numéro dossier", "numero_dossier"],
                ["Téléphone", "contact"],
                ["Adresse", "adresse"],
                ["Code postal", "codePostal"],
                ["Commune", "commune"],
                ["Métier", "metier"],
              ].map(([label, key]) => (
                <div key={key}>
                  <label>{label}</label>
                  <input
                    value={editing[key] || ""}
                    onChange={(e) =>
                      setEditing((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                  />
                </div>
              ))}
              <div>
                <label>Sexe</label>
                <select
                  value={editing.gender || ""}
                  onChange={(e) =>
                    setEditing((prev) => ({ ...prev, gender: e.target.value }))
                  }
                >
                  <option value="">--</option>
                  <option value="masculin">Masculin</option>
                  <option value="feminin">Féminin</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label>Hébergement</label>
                <select
                  value={editing.hebergement || ""}
                  onChange={(e) =>
                    setEditing((prev) => ({
                      ...prev,
                      hebergement: e.target.value,
                    }))
                  }
                >
                  <option value="">--</option>
                  <option value="oui">Oui</option>
                  <option value="non">Non</option>
                </select>
              </div>
              <div className="adhesion-group">
                <label>Type d’adhésion</label>
                <div className="adhesion-options">
                  {["adherent", "benevole", "organisateur", "autre"].map(
                    (type) => (
                      <button
                        type="button"
                        key={type}
                        className={`adhesion-pill ${editing.typeAdhesion?.includes(type) ? "active" : ""}`}
                        onClick={() =>
                          setEditing((prev) => ({
                            ...prev,
                            typeAdhesion: prev.typeAdhesion?.includes(type)
                              ? prev.typeAdhesion.filter((t) => t !== type)
                              : [...(prev.typeAdhesion || []), type],
                          }))
                        }
                      >
                        {type}
                      </button>
                    ),
                  )}
                </div>
              </div>
              <div>
                <label>Date de naissance</label>
                <input
                  type="date"
                  value={editing.naissance || ""}
                  onChange={(e) =>
                    setEditing((prev) => ({
                      ...prev,
                      naissance: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label>Date d’adhésion</label>
                <input
                  type="date"
                  value={editing.adhesion || ""}
                  onChange={(e) =>
                    setEditing((prev) => ({
                      ...prev,
                      adhesion: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label>Date d’expiration</label>
                <input
                  type="date"
                  value={editing.dateExpiration || ""}
                  onChange={(e) =>
                    setEditing((prev) => ({
                      ...prev,
                      dateExpiration: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label>Suivi administratif</label>
                <textarea
                  value={(editing.suivi_administratif || []).join(", ")}
                  onChange={(e) =>
                    setEditing((prev) => ({
                      ...prev,
                      suivi_administratif: e.target.value
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean),
                    }))
                  }
                  placeholder="Séparez les éléments par des virgules"
                />
              </div>
            </div>
            <div className="edit-actions">
              <button
                type="button"
                onClick={async () => {
                  const updated = { ...editing };
                  const toastId = toast.loading("Mise à jour...");
                  try {
                    const fd = new FormData();
                    fd.append("id", updated.id);
                    [
                      "nom",
                      "prenom",
                      "gender",
                      "email",
                      "contact",
                      "adresse",
                      "codePostal",
                      "commune",
                      "metier",
                      "hebergement",
                      "numero_dossier",
                      "naissance",
                      "adhesion",
                      "dateExpiration",
                    ].forEach((key) => fd.append(key, updated[key] || ""));
                    fd.append(
                      "typeAdhesion",
                      JSON.stringify(updated.typeAdhesion || []),
                    );
                    fd.append(
                      "suivi_administratif",
                      JSON.stringify(updated.suivi_administratif || []),
                    );
                    if (updated.newPhoto) fd.append("photo", updated.newPhoto);
                    const res = await axios.post("/api/updateMember.php", fd);
                    if (!res.data?.success)
                      throw new Error(
                        res.data?.error || "Mise à jour impossible",
                      );
                    setMember((prev) => ({
                      ...prev,
                      ...updated,
                      photo: updated.newPhoto
                        ? res.data.photo || updated.photo
                        : updated.photo,
                    }));
                    setEditing(null);
                    if (cameraOn) stopCamera(videoRef, setCameraOn);
                    toast.success("Membre mis à jour", { id: toastId });
                    const refreshed = await axios.get(
                      `/api/getMember.php?id=${id}`,
                    );
                    if (refreshed.data?.success)
                      setMember({
                        ...refreshed.data.member,
                        cotisation:
                          refreshed.data.member.cotisation ??
                          refreshed.data.member.cotisation_amount ??
                          "",
                        cotisationStatus:
                          refreshed.data.member.cotisationStatus ??
                          refreshed.data.member.cotisation_status ??
                          "",
                      });
                  } catch (err) {
                    console.error(err);
                    toast.error(
                      err.message || "Erreur lors de la mise à jour",
                      { id: toastId },
                    );
                  }
                }}
              >
                💾 Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  if (cameraOn) stopCamera(videoRef, setCameraOn);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showCommentsModal && (
        <div
          className="mp-comments-admin-overlay"
          onClick={() => setShowCommentsModal(false)}
          role="presentation"
        >
          <section
            className="mp-comments-admin-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Commentaires de ${member.nom} ${member.prenom}`}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="mp-comments-admin-header">
              <div className="mp-comments-admin-avatar">
                {member.photo ? (
                  <img
                    src={`/api/image.php?file=${encodeURIComponent(member.photo)}`}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <span aria-hidden="true">♙</span>
                )}
              </div>
              <div className="mp-comments-admin-title">
                <h2>
                  {member.nom} {member.prenom}
                </h2>
                <p>Commentaires et suivi du dossier</p>
              </div>
              <button
                className="mp-comments-admin-close"
                type="button"
                aria-label="Fermer les commentaires"
                onClick={() => setShowCommentsModal(false)}
              >
                ×
              </button>
            </header>

            <div className="mp-comments-admin-list">
              {commentsLoading ? (
                <div className="mp-comments-admin-empty">
                  Chargement des commentaires…
                </div>
              ) : comments.length > 0 ? (
                comments.map((comment, index) => (
                  <article
                    className="mp-comments-admin-item"
                    key={
                      comment.id ||
                      comment.comment_id ||
                      `${comment.created_at}-${index}`
                    }
                  >
                    <span
                      className={`mp-comments-admin-dot ${index === 0 ? "first" : ""}`}
                    />
                    <div className="mp-comments-admin-meta">
                      <span>
                        ▦{" "}
                        {comment.created_at
                          ? new Date(comment.created_at).toLocaleString("fr-FR")
                          : "Date inconnue"}
                      </span>
                      <strong>
                        {comment.author_name ||
                          comment.admin_name ||
                          comment.created_by_name ||
                          "Administrateur"}
                      </strong>
                    </div>
                    {(() => {
                      const commentId = String(
                        comment.id ??
                          comment.comment_id ??
                          comment.created_at ??
                          index,
                      );
                      const translation = commentTranslations[commentId] || {};
                      const selectedLanguage =
                        translation.selectedLanguage || "original";
                      const displayedComment =
                        selectedLanguage === "original"
                          ? comment.commentaire || ""
                          : translation[selectedLanguage] ||
                            comment.commentaire ||
                            "";
                      const langButtonClass = (active) =>
                        `mp-comment-translate-btn${active ? " is-active" : ""}`;
                      return (
                        <>
                          {Number(comment.is_deleted) === 1 ? (
                            <p className="mp-comments-admin-deleted">
                              Commentaire supprimé par{" "}
                              {comment.deleted_by || "un administrateur"}.
                              L’historique est conservé.
                            </p>
                          ) : (
                            <p className="mp-comments-admin-text">
                              {displayedComment}
                            </p>
                          )}
                          {Number(comment.is_deleted) !== 1 && (
                            <div className="mp-comment-translation-controls">
                              <button
                                type="button"
                                className={langButtonClass(
                                  selectedLanguage === "original",
                                )}
                                onClick={() =>
                                  setCommentTranslations((prev) => ({
                                    ...prev,
                                    [commentId]: {
                                      ...prev[commentId],
                                      selectedLanguage: "original",
                                    },
                                  }))
                                }
                                aria-pressed={selectedLanguage === "original"}
                              >
                                ▤ <span>Original</span>
                              </button>
                              <button
                                type="button"
                                className={langButtonClass(
                                  selectedLanguage === "en",
                                )}
                                onClick={() =>
                                  translateMemberComment(comment, "en")
                                }
                                disabled={!!translatingComments[commentId]}
                                aria-pressed={selectedLanguage === "en"}
                              >
                                <span
                                  className="mp-translation-flag mp-flag-en"
                                  aria-hidden="true"
                                >
                                  <i />
                                </span>
                                <span>
                                  {translatingComments[commentId] &&
                                  selectedLanguage !== "en"
                                    ? "Translating…"
                                    : "EN"}
                                </span>
                              </button>
                              <button
                                type="button"
                                className={langButtonClass(
                                  selectedLanguage === "fr",
                                )}
                                onClick={() =>
                                  translateMemberComment(comment, "fr")
                                }
                                disabled={!!translatingComments[commentId]}
                                aria-pressed={selectedLanguage === "fr"}
                              >
                                <span
                                  className="mp-translation-flag mp-flag-fr"
                                  aria-hidden="true"
                                >
                                  <i />
                                </span>
                                <span>
                                  {translatingComments[commentId] &&
                                  selectedLanguage !== "fr"
                                    ? "Traduction…"
                                    : "FR"}
                                </span>
                              </button>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {comment.updated_at &&
                      comment.updated_at !== comment.created_at &&
                      Number(comment.is_deleted) !== 1 && (
                        <small className="mp-comments-admin-edited">
                          Modifié le{" "}
                          {new Date(comment.updated_at).toLocaleString("fr-FR")}
                        </small>
                      )}
                  </article>
                ))
              ) : (
                <div className="mp-comments-admin-empty">
                  <span aria-hidden="true">▱</span>
                  <strong>Aucun commentaire pour le moment</strong>
                  <p>
                    Ajoute un commentaire pour commencer le suivi de ce membre.
                  </p>
                </div>
              )}
            </div>

            <footer className="mp-comments-admin-compose">
              <label htmlFor="mp-new-member-comment">
                Ajouter un commentaire
              </label>
              <textarea
                id="mp-new-member-comment"
                value={newCommentText}
                onChange={(e) =>
                  setNewCommentText(e.target.value.slice(0, 1000))
                }
                placeholder="Écrire un commentaire sur ce membre…"
                maxLength={1000}
                rows={3}
              />
              <div className="mp-comments-admin-compose-bottom">
                <span>{newCommentText.length} / 1000</span>
                <div>
                  <button
                    type="button"
                    className="mp-comments-admin-cancel"
                    onClick={() => setShowCommentsModal(false)}
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    className="mp-comments-admin-add"
                    onClick={addMemberComment}
                    disabled={!newCommentText.trim()}
                  >
                    ＋ Ajouter
                  </button>
                </div>
              </div>
            </footer>
          </section>
        </div>
      )}

      {showChat && (
        <div className="mp-modal-overlay">
          <div className="mp-chat-modal">
            <div className="mp-chat-header">
              <div>
                <span className="mp-icon">▣</span>
                <div>
                  <strong>Messages</strong>
                  <small>
                    {member.nom} {member.prenom}
                  </small>
                </div>
              </div>
              <button onClick={() => setShowChat(false)}>×</button>
            </div>
            <div className="mp-chat-box">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`mp-message ${msg.sender?.toLowerCase()}`}
                >
                  {msg.message}
                  {msg.sender === "admin" && (
                    <span className="ticks">{msg.is_read ? "✔✔" : "✔"}</span>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="mp-chat-input">
              <input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Écrire un message..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendAdminMessage();
                }}
              />
              <button className="mp-primary" onClick={sendAdminMessage}>
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MemberPage;
